import React, { useContext } from 'react'
import { Box, Button, Typography, LinearProgress, Tooltip, Paper, Chip, useMediaQuery } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { UserContext, SkillData, categories, skillFrequency, SkillFrequency } from "../UserAndProfile/UserContext";
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import {Card, CardHeader, CardContent} from '@mui/material';
import Grid from '@mui/material/Grid';
import { useNavigate } from 'react-router-dom';
//import theme from "../../muiTheme"

/*type ImprovementArea = {
  skill: string
  attempted: number
  percentCorrect: number
  frequency: number
  category: 'Math' | 'Reading and Writing'
  suggestedChallenge: string
}*/
interface ImprovementAreasProps {
  containerStyle?: React.CSSProperties; // Optional styling for the container
}

const StyledButton = styled(Button)(({ theme }) => ({
  width: '100%',
  justifyContent: 'flex-start',
  fontSize: '0.75rem',
  height: 'auto',
  padding: '0.5rem 0.75rem',
  textTransform: 'none',
  lineHeight: 1.5,
  '& strong': {
    fontWeight: 'bold',
  },
}));

const ProgressContainer = styled(Box)(({ theme }) => ({
  height: '0.5rem',
  width: '100%',
  backgroundColor: '#e0e0e0',
  borderRadius: '0.25rem',
  position: 'relative',
}));

const StyledLinearProgress = styled(LinearProgress)(({ theme }) => ({
  height: '100%',
  borderRadius: '0.25rem',
  backgroundColor: '#e0e0e0',
  '& .MuiLinearProgress-bar': {
    backgroundColor: theme.palette.custom.green,
  },
}));

const GoalIndicator = styled('div')(({ theme }) => ({
  position: 'absolute',
  top: 0,
  bottom: 0,
  borderLeft: `1px solid ${theme.palette.primary.main}`,
}));

interface Props {
  category: 'Math' | 'Reading and Writing';  // 'Math' | 'Reading and Writing'
  limitToTopTwo?: boolean;  // Optional: limit to top 2 skills instead of 3
}

export function ImprovementAreasContainer({ containerStyle }: ImprovementAreasProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Box sx={{ display: 'flex', flexDirection: {xs: 'column', md: 'row'}, justifyContent: 'space-evenly', gap: 3, ...containerStyle }}>
      <Grid sx={{flex: 1, gridColumn: { xs: "span 12", md: "span 6" } }}>
        <Card sx={{width: '100%', p: 1}}>
          <CardHeader 
            sx={{ pb: 1, pt: 0 }} 
            title={<Typography variant="h6" color="primary.main">Top {isMobile ? '2' : '3'} Reading & Writing Skills to Improve</Typography>} 
          />
          <CardContent sx={{ py: 0, mb: 0, '&:last-child': { pb: 2 } }}>
            <ImprovementAreasVisualization category="Reading and Writing" limitToTopTwo={isMobile}/>
          </CardContent>
        </Card>
      </Grid>
      <Grid sx={{flex: 1, gridColumn: { xs: "span 12", md: "span 6" } }}>
        <Card sx={{width: '100%', p: 1}}>
          <CardHeader 
            sx={{ py: 1 }} 
            title={<Typography variant="h6" color="primary.main">Top {isMobile ? '2' : '3'} Math Skills to Improve</Typography>} 
          />
          <CardContent sx={{ py: 0, '&:last-child': { pb: 2 } }}>
            <ImprovementAreasVisualization category="Math" limitToTopTwo={isMobile}/>
          </CardContent>
        </Card>
      </Grid>
    </Box>
  );
}

export function ImprovementAreasVisualization({ category, limitToTopTwo = false }: Props) {
  const { user } = useContext(UserContext);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // 1. Calculate performance for each skill
  const calculateSkillPerformance = (skillName: string) => {
    const skillKey = skillName.toLowerCase().replace(/ /g, '_');
    //console.log(`Calculating performance for skill: ${skillKey}`);
    const skillData = user?.skills[skillKey];
    if (!skillData) {
      //console.log(`No skill data found for: ${skillKey}`);
      return 0;
    }
    
    const attempted = skillData.overallQuestionsAttempted || 0;
    const correct = skillData.overallQuestionsCorrect || 0;
    console.log(`${skillName}: ${correct}/${attempted} = ${attempted > 0 ? (correct / attempted) * 100 : 0}%`);
    
    return attempted > 0 ? (correct / attempted) * 100 : 0;
  };

  // 2. Get priority score (frequency/performance ratio)
  /*TODO: Revisit this to make it more accurate based on user's usage of the app
  - If it's been many days since practicing a skill, gradually increase priority to promote spaced repetition
  - Consider differences in lower initial starting skill students vs. higher achieving so this works for both.
  */
  const getPriorityScore = (skillName: string, performance: number) => {
    const frequency = skillFrequency[skillName as keyof SkillFrequency] || 0.02;
    const performanceScore = performance === 0 ? 20 : Math.max(20, performance); //20 is the base if they haven't attempted any questions
    const frequencyScore = frequency * 100;
    return frequencyScore * (1 - (performanceScore / 100));
  };

  // 3. Suggest difficulty for the user to practice based on their performance
  const getSuggestedDifficulty = (performance: number): 'Easy' | 'Medium' | 'Hard' => {
    if (performance > 0 && performance < 40) return 'Easy';
    if (performance >= 40 && performance < 70) return 'Medium';
    if (performance >= 70) return 'Hard';
    return 'Medium';
  };

  // 4. Add function to get difficulty-specific attempts  
  const getDifficultyAttempts = (skillName: string, difficulty: 'Easy' | 'Medium' | 'Hard') => {
    const skillKey = skillName.toLowerCase().replace(/ /g, '_');
    const skillData = user?.skills[skillKey];
    if (!skillData) {
      //console.log(`No skill data found for: ${skillKey}`);
      return 0;
    }
    
    switch (difficulty) {
      case 'Easy':
        return skillData.currentTestEasyQuestionsAttempted || 0;
      case 'Medium':
        return skillData.currentTestMediumQuestionsAttempted || 0;
      case 'Hard':
        return skillData.currentTestHardQuestionsAttempted || 0;
      default:
        return 0;
    }
  };

  // 5. Generate improvement areas by sorting by priority score (top 2 or 3 based on limitToTopTwo)
  const topSkills = React.useMemo(() => {
    if (!user) return [];
    
    const categorySkills = categories[category];
    const skillsWithScores = categorySkills.map(skillName => {
      const performance = calculateSkillPerformance(skillName);
      const priorityScore = getPriorityScore(skillName, performance);
      const suggestedDifficulty = getSuggestedDifficulty(performance);
      
      // Get total attempts and difficulty-specific attempts
      const skillData = user.skills[skillName];
      const totalAttempted = skillData?.overallQuestionsAttempted || 0;
      const difficultyAttempted = getDifficultyAttempts(skillName, suggestedDifficulty);
      
      return { 
        skillName, 
        performance, 
        priorityScore, 
        suggestedDifficulty, 
        totalAttempted,
        difficultyAttempted
      };
    });
    
    skillsWithScores.sort((a, b) => b.priorityScore - a.priorityScore);
    return skillsWithScores.slice(0, limitToTopTwo ? 2 : 3);
  }, [user, category, limitToTopTwo]);

  if (!user) return <Typography variant="body2">Create your profile or log in to see your improvement areas!</Typography>;

  

  
  /*const topSkills = categories[category]
    .map(skillName => {
      const skillKey = skillName.toLowerCase().replace(/ /g, '_');
      const performance = calculateSkillPerformance(skillKey);
      //console.log(`Performance for ${skillName}: ${performance}`);
      const frequency = (skillFrequency[skillName] || 0.02) * 100; //skillFrequency is "Function of Sentence" vs. "function-of-sentence" format
      
      return {
        skillName,
        performance,
        frequency,
        priorityScore: getPriorityScore(skillName, performance),
        suggestedDifficulty: getSuggestedDifficulty(performance)
      };
    })
    .sort((a, b) => b.priorityScore - a.priorityScore)
    .slice(0, 3);*/

  //************************************************************************************* */
  // 5. Generate challenge text ****Super important part to revisit this based on user's usage of the app!!
  //Just randomize the number of questions in a range (4-6) for funzies? Could also depend on when I finish the AI summary of the question sets
  //These challenges can also be dynamically generated by an AI based on the user's performance and the question sets
  const generateChallenge = (skill: typeof topSkills[0]) => {
    const questionCount = skill.performance === 0 ? 4 : 5 //4 questions at easy, 5 at medium
    
    return `Complete ${questionCount} ${skill.skillName} questions at ${skill.suggestedDifficulty} difficulty`;
  };
  
  const getGoalPercentage = (targetScore: number) => {
    // Example formula: (targetScore - currentScore) / totalPossibleScore * 100
    //This shouldn't be used: parseInt(user.targetScores.readingWriting) + parseInt(user.targetScores.math) ||
    const totalPossibleScore = 1600; // Assuming SAT total score
    //console.log(`Goal Percentage = targetScore (${targetScore}) / totalPossibleScore (${totalPossibleScore})`);
    const rawPercentage = ((targetScore / totalPossibleScore) * 100)
    return rawPercentage.toFixed(0);
  };

  // Get combined goal score
  const goal = user.targetScores?.readingWriting && user.targetScores?.math
    ? parseInt(user.targetScores.readingWriting) + parseInt(user.targetScores.math)
    : null;

  // New handler function for practicing a skill
  const handlePracticeSkill = (skillName: string, difficulty: 'Easy' | 'Medium' | 'Hard') => {
    const subjectPath = category === 'Math' ? 'math' : 'reading-and-writing';
    // Encode skillName in case it has spaces or special characters
    const encodedSkillName = encodeURIComponent(skillName);
    const targetUrl = `/skill-practice?skill=${encodedSkillName}&difficulty=${difficulty}`; ///${subjectPath}
    console.log(`Navigating to: ${targetUrl}`); // For debugging
    navigate(targetUrl);
  };

  return (
    <Box sx={{display: 'flex', flexDirection: 'column', gap: 2, px: 0, pb: 0, width: '100%'}}>
      
      <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', gap: 2 }}>
        {topSkills.map((skill, index: number) => (
          <Box key={skill.skillName} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {/*StyledButton looks like: "Complete 5 Linear Equations questions at Medium difficulty - 4.5% frequency on test"*/}
            <StyledButton 
              variant="outlined"
              //Temporarily disabled the clicking on 2/7 while it's in development.
              //Eventually this onClick should call startQuestionSeries in SkillButtonsAndQuestionGenerator.tsx
              onClick={() => handlePracticeSkill(skill.skillName, skill.suggestedDifficulty)}
            >
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
                <Typography component="span" sx={{ fontSize: {xs: theme.typography.body3.fontSize, sm: theme.typography.body2.fontSize} }}>Practice 5{' '}</Typography>
                <Typography component="span" sx={{ fontWeight: 'bold', fontSize: {xs: theme.typography.body3.fontSize, sm: theme.typography.body2.fontSize} }}>
                  {skill.suggestedDifficulty} {skill.skillName}
                </Typography>{' '}
                <Typography component="span" sx={{ fontSize: {xs: theme.typography.body3.fontSize, sm: theme.typography.body2.fontSize} }}>questions{' '}</Typography>
                {/*<Typography component="span">{skill.difficultyAttempted} attempts</Typography>*/}
              </Box> 
            </StyledButton>
            
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="caption" color="text.secondary">
                  {typeof skill.performance === 'number' 
                    ? `${skill.performance.toFixed(0)}% correct across all difficulties`
                    : '0% correct across all difficulties'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {goal ? `${getGoalPercentage(goal)}% needed for ${goal}` : ''}
                </Typography>
              </Box>
              <ProgressContainer>
                <StyledLinearProgress variant="determinate"
                  value={typeof skill.performance === 'number' 
                    ? Math.min(100, Math.max(0, skill.performance))  // Clamp between 0-100
                    : 0}
                />
                  {goal ? (
                  <Tooltip title={`Goal: ${getGoalPercentage(goal)}% correct required to meet SAT score goal`}>
                    <GoalIndicator
                      style={{ left: `${Number(getGoalPercentage(goal))}%` }}
                    />
                  </Tooltip>
                  ) : null}
              </ProgressContainer>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  )
}

//Initially removed in February 2025, removed again on 4/15 after removing challenges. This is probably garbage,
//but I'll keep it here for now.
/* *****************Revisit and implement this later Challenge Journey ******************** 
      <Box sx={{ mt: 2, px: 2 }}>
        <Typography variant="h5" sx={{ mb: 2 }}>Your Challenge Journey</Typography>
        
        <Box sx={{ 
          position: 'relative',
          maxHeight: '400px',
          overflow: 'hidden',
          '&:hover': {
            overflow: 'overlay', // Show scrollbar on hover
          },
          // Add padding-right to account for scrollbar width
          pr: 1,
          mr: -1,
          // Add custom scrollbar styling
          '&::-webkit-scrollbar': {
            width: '8px',
            backgroundColor: 'transparent',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'transparent',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(0,0,0,0.2)',
            borderRadius: '4px',
            '&:hover': {
              backgroundColor: 'rgba(0,0,0,0.3)',
            },
          },
        }}>
          {/* Timeline line needs to be inside scrollable container 
          <Box sx={{
            position: 'absolute',
            left: 4,
            top: 0,
            bottom: 0,
            width: 2,
            bgcolor: 'grey.300',
          }} />
          
          {filteredChallenges.map(challenge => (
            <Box
              key={challenge.id}
              sx={{
                display: 'flex',
                mb: 3,
                position: 'relative',
                pl: 4
              }}
            >
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  position: 'absolute',
                  left: 0,
                  top: 6,
                  bgcolor: challenge.completed ? 'success.main' : 'primary.main',
                }}
              />
              
              <Paper
                sx={{
                  p: 2,
                  flex: 1,
                  width: 'calc(100% - 16px)',
                  borderRadius: 2,
                  bgcolor: challenge.completed ? 'success.light' : 'background.paper',
                  border: 1,
                  borderColor: challenge.completed ? 'success.main' : 'primary.main',
                  cursor: 'pointer',
                  '&:hover': {
                    bgcolor: challenge.completed ? 'success.light' : 'rgba(0, 0, 0, 0.04)',
                  },
                }}
              >
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center'
                }}>
                  <Typography
                    variant="subtitle1"
                    color={challenge.completed ? 'success.main' : 'primary.main'}
                  >
                    {challenge.text}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Chip
                      label={challenge.completed ? 'Completed' : 'In Progress'}
                      size="small"
                      color={challenge.completed ? 'success' : 'primary'}
                      sx={{ mr: 1 }}
                    />
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMenuOpen(e, challenge.id);
                      }}
                    >
                      <MoreVertIcon />
                    </IconButton>
                  </Box>
                </Box>
              </Paper>
            </Box>
          ))}
        </Box>
      </Box>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem 
          onClick={handleCompleteChallenge}
          disabled={challenges.find(c => c.id === selectedChallengeId)?.completed}
        >
          Mark as Completed
        </MenuItem>
        <MenuItem 
          onClick={handleRemoveChallenge}
          sx={{ color: 'error.main' }}
        >
          Remove Challenge
        </MenuItem>
      </Menu>*/